import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useGetIdentity } from './AuthContext';
import axiosInstance from './../lib/util/axios';
import { API_URL } from './../config';
import { useCohortWorkspace } from '../features/cohort-workspace/CohortWorkspaceContext';

// Types
interface Program {
  _id: string;
  name: string;
  school: string;
  description?: string;
  cohortId: string;
  cohortProgramId?: string;
}

interface UserIdentity {
  id: string;
  firstName: string;
  lastName: string;
  programs?: string[];
  role?: string;
}

interface TutorProgramContextType {
  loading: boolean;
  availablePrograms: Program[];
  selectedProgram: Program | null;
  setSelectedProgram: (program: Program) => void;
  error: string | null;
}

// Create context
const TutorProgramContext = createContext<TutorProgramContextType>({
  loading: true,
  availablePrograms: [],
  selectedProgram: null,
  setSelectedProgram: () => { },
  error: null
});

// Context provider component
export const TutorProgramProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { cohortId } = useCohortWorkspace();
  const [loading, setLoading] = useState<boolean>(true);
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: user } = useGetIdentity();

  // Initial load of programs
  useEffect(() => {
    const fetchTutorPrograms = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to restore previously selected program
        if (!cohortId) {
          setError('Select a cohort workspace to continue.');
          setLoading(false);
          return;
        }
        const savedProgram = localStorage.getItem(`selectedTutorProgram:${cohortId}`);
        let savedProgramId: string | null = null;

        if (savedProgram) {
          try {
            const parsed = JSON.parse(savedProgram);
            savedProgramId = parsed.id;
          } catch (e) {
            // Invalid saved data, ignore
          }
        }

        // Get program list from user identity
        const programNames: string[] = [];

        if (user?.programs && user.programs.length > 0) {
          programNames.push(...user.programs);
        } else {
          // Fallback to localStorage
          const userData = localStorage.getItem('userData');
          if (userData) {
            const parsedUser = JSON.parse(userData);
            if (parsedUser.programs && parsedUser.programs.length > 0) {
              programNames.push(...parsedUser.programs);
            }
          }
        }

        if (programNames.length === 0) {
          setError('Could not determine your programs. Please contact an administrator.');
          setLoading(false);
          return;
        }

        const intakeResponse = await axiosInstance.get(`${API_URL}/api/cohorts/${cohortId}/intakes`);
        const intakes = intakeResponse.data?.intakes || [];
        const programsData: Program[] = [];
        for (const name of programNames) {
          try {
            // Note the fix here: using "by-name" instead of "byName"
            const response = await axiosInstance.get(`${API_URL}/api/programs/by-name/${name}`);
            if (response.data) {
              const intake = intakes.find((candidate: any) => {
                const intakeProgramId = typeof candidate.program === 'object' ? candidate.program?._id : candidate.program;
                return String(intakeProgramId) === String(response.data._id);
              });
              if (intake) programsData.push({ ...response.data, cohortId, cohortProgramId: intake._id });
            }
          } catch (err) {
            console.error(`Error fetching program ${name}:`, err);
          }
        }

        if (programsData.length === 0) {
          setError('Failed to fetch program information');
          setLoading(false);
          return;
        }

        setAvailablePrograms(programsData);

        // Auto-select program based on saved selection or default to first if only one
        if (savedProgramId) {
          const saved = programsData.find(p => p._id === savedProgramId);
          if (saved) {
            setSelectedProgram(saved);
          } else if (programsData.length === 1) {
            setSelectedProgram(programsData[0]);
          }
        } else if (programsData.length === 1) {
          setSelectedProgram(programsData[0]);
        }
      } catch (error) {
        console.error('Error fetching tutor programs:', error);
        setError('Failed to fetch program information');
      } finally {
        setLoading(false);
      }
    };

    fetchTutorPrograms();
  }, [user, cohortId]);

  // Handler for program selection
  const handleSelectProgram = (program: Program) => {
    setSelectedProgram(program);

    // Store selection in localStorage for persistence
    localStorage.setItem(`selectedTutorProgram:${program.cohortId}`, JSON.stringify({
      id: program._id,
      name: program.name
    }));
  };

  // Context value
  const contextValue: TutorProgramContextType = {
    loading,
    availablePrograms,
    selectedProgram,
    setSelectedProgram: handleSelectProgram,
    error
  };

  return (
    <TutorProgramContext.Provider value={contextValue}>
      {children}
    </TutorProgramContext.Provider>
  );
};

// Custom hook for using the context
export const useTutorProgram = () => useContext(TutorProgramContext);

export default TutorProgramContext;
